import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { identityVerificationService } from "../../../services/identityVerificationService";
import { STORAGE_KEYS } from "../../../types/storage";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";



// 리다이렉션 지연 시간 (ms)
const REDIRECT_DELAY_MS = 1000;

type CallbackStatus = "processing" | "success" | "error";
type UserType = "existing" | "new" | null;

const IdentityVerificationCallbackScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [status, setStatus] = useState<CallbackStatus>("processing");
  const [userType, setUserType] = useState<UserType>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const processedRef = useRef(false);
  const userTypeRef = useRef<UserType>(null);

  const processCallback = useCallback(async () => {
    // 중복 처리 방지
    if (processedRef.current) return;
    processedRef.current = true;

    const identityVerificationId = searchParams.get("identityVerificationId");
    const code = searchParams.get("code");
    const message = searchParams.get("message");

    // identityVerificationId가 없으면 잘못된 접근
    if (!identityVerificationId) {
      setStatus("error");
      setErrorMessage("잘못된 접근입니다. 본인인증을 다시 시도해주세요.");
      return;
    }

    // PortOne에서 에러 코드 반환
    if (code) {
      console.error("[PortOne] 본인인증 실패:", { code, message });
      setStatus("error");
      setErrorMessage(message || "본인인증에 실패했습니다.");
      return;
    }

    // sessionStorage에서 전화번호 가져오기
    const phoneNumber = sessionStorage.getItem(STORAGE_KEYS.PHONE_NUMBER);

    if (!phoneNumber) {
      setStatus("error");
      setErrorMessage("본인인증 정보가 만료되었습니다. 다시 시도해주세요.");
      return;
    }

    try {
      // 백엔드에서 본인인증 결과 확인
      const confirmResult =
        await identityVerificationService.confirmVerification({
          identityVerificationId,
          phoneNumber,
        });

      if (confirmResult.userExists) {
        // 기존 사용자 - 로그인 처리
        setStatus("success");
        setUserType("existing");

        if (confirmResult.accessToken && confirmResult.refreshToken) {
          login(confirmResult.accessToken, confirmResult.refreshToken);
        }
      } else {
        // 신규 사용자 - 약관동의 페이지로 이동 준비
        setStatus("success");
        setUserType("new");
      }
    } catch (err) {
      console.error("본인인증 콜백 처리 실패:", err);
      setStatus("error");
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "본인인증 처리 중 오류가 발생했습니다."
      );
    }
  }, [searchParams, login]);

  // 콜백 처리 실행
  useEffect(() => {
    processCallback();
  }, [processCallback]);

  // 성공 시 리다이렉션 (타이머 정리 포함)
  useEffect(() => {
    if (status !== "success") return;

    const timer = setTimeout(() => {
      const path = userType === "existing" ? "/" : "/auth/terms";
      navigate(path, { replace: true });
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [status, userType, navigate]);

  // userType 변경 시 ref 업데이트 (cleanup에서 참조하기 위함)
  useEffect(() => {
    userTypeRef.current = userType;
  }, [userType]);

  // 컴포넌트 언마운트 시 sessionStorage 정리
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(STORAGE_KEYS.PHONE_NUMBER);
      // 신규 사용자인 경우 약관 동의 페이지에서 identityVerificationId가 필요하므로 유지
      if (userTypeRef.current !== "new") {
        sessionStorage.removeItem(STORAGE_KEYS.VERIFICATION_ID);
      }
    };
  }, []);

  const handleRetry = () => {
    navigate("/auth/identity-verification", { replace: true });
  };

  // 처리 중 UI
  if (status === "processing") {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-6">
        <div className="text-center">
          <svg
            className="animate-spin mx-auto h-16 w-16 text-blue-600 mb-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            본인인증 결과 확인 중
          </h2>
          <p className="text-gray-600">잠시만 기다려주세요...</p>
        </div>
      </div>
    );
  }

  // 성공 UI
  if (status === "success") {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <Icon name="check" className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            본인인증이 완료되었습니다
          </h2>
          <p className="text-gray-600 mb-6">
            {userType === "existing" ? (
              <>
                환영합니다! 로그인이 완료되었습니다.
                <br />
                <span className="text-sm text-gray-500">
                  잠시 후 홈 화면으로 이동합니다...
                </span>
              </>
            ) : (
              <>
                서비스 이용을 위해 약관 동의가 필요합니다.
                <br />
                <span className="text-sm text-gray-500">
                  잠시 후 약관동의 화면으로 이동합니다...
                </span>
              </>
            )}
          </p>
          <div className="flex justify-center">
            <svg
              className="animate-spin h-5 w-5 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // 실패 UI
  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-6">
      <div className="text-center max-w-sm w-full">
        <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
          <Icon name="x" className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-3">
          본인인증에 실패했습니다
        </h2>
        <p className="text-gray-600 mb-2">{errorMessage}</p>
        <p className="text-sm text-gray-500 mb-8">
          문제가 지속되면 고객센터로 문의해주세요.
        </p>
        <Button
          fullWidth
          size="lg"
          onClick={handleRetry}
          className="bg-blue-600 hover:bg-blue-700 py-4 text-base"
        >
          다시 시도하기
        </Button>
      </div>
    </div>
  );
};

export default IdentityVerificationCallbackScreen;
